import { createLucideIcon } from "lucide-react";

/**
 * Los tres iconos de los sistemas de Vecipp — **POS, Contabilidad y Nómina**, dibujados a medida.
 *
 * 🔑 **Por qué existen, y por qué se dibujaron en vez de elegirlos de la librería** (2026-09-12, a
 * pedido del usuario: *«POS tiene un carrito en todas las apps y el POS debe llevar es una caja
 * registradora; aclara mejor los iconos de POS, Contabilidad y Nómina en absolutamente todas las
 * páginas»*):
 *
 * - **El carrito estaba mal de fondo.** Un carrito es lo que empuja **quien compra**; POS es la
 *   herramienta de **quien vende**. La librería tiene 2048 iconos y **ninguno es una caja
 *   registradora** — comprobado, no hay `cash`, `register` ni `till`.
 * - **Y no había una sola fuente de verdad.** Los tres estaban escritos a mano en **13 archivos de 5
 *   proyectos**, y ya se contradecían: Contabilidad era `Calculator` en 12 sitios y `BookOpen` en la
 *   pantalla de equipo del panel; Nómina, `Wallet` en 12 y `Users` en esa misma. Nadie lo notó porque
 *   nada obligaba a que coincidieran. Ahora salen todos de `ICONOS_SISTEMA`.
 *
 * 🔑 **Se construyen con `createLucideIcon()`, no como `<svg>` sueltos.** Es la fábrica que la propia
 * librería exporta (firma idéntica en las 3 versiones instaladas del ecosistema: 1.34, 1.37 y 1.38), y
 * devuelve **exactamente el tipo `LucideIcon`**. Eso no es cosmético: los sitios que los consumen están
 * tipados como `LucideIcon` o `typeof ShoppingCart`, así que siguen compilando sin tocar un solo tipo, y
 * los iconos heredan gratis `size`, `strokeWidth`, `absoluteStrokeWidth`, `color` y la clase `lucide`.
 * Un `<svg>` a mano habría obligado a cambiar los tipos de los 13 archivos y a reimplementar todo eso.
 *
 * ⚠️ **Este archivo existe CUATRO veces y las cuatro copias son idénticas**: en `Vecipp-Core`
 * (que sirve a Dashboard, POS, Contabilidad, Nómina y Auth vía `transpilePackages`), y en
 * `Vecipp-Landing`, `Vecipp-Ayuda` y `Vecipp-Admin`, que **no consumen `Vecipp-Core`** — regla escrita
 * en el `CLAUDE.md` de la raíz, y no se cambia por un icono. **Si se retoca un trazado, se retocan las
 * cuatro**, o los sistemas vuelven a verse distintos según la app.
 *
 * Los tres están dibujados en la retícula de la librería: `viewBox` 24×24, trazo 2, extremos y uniones
 * redondeados, sin relleno. Comprobados a 16, 20, 24 y 40 px y en blanco sobre el verde de marca —
 * **16px es el tamaño real de la mayoría de los sitios** (`size-4`), así que es el que manda.
 */

/**
 * **POS — la caja registradora.** Cuerpo ancho, la pantalla apoyada a la izquierda, dos teclas a su
 * derecha y la raya del cajón con su tirador.
 *
 * ⚠️ **Las dos teclas son lo que la separa de una impresora.** Se probó el mismo dibujo sin ellas y el
 * resultado era un cuerpo con una caja encima: idéntico a una impresora. Con la pantalla a un lado y las
 * teclas al otro se lee como un mostrador.
 */
export const IconoPos = createLucideIcon("IconoPos", [
  ["rect", { x: "2", y: "11", width: "20", height: "10", rx: "2", key: "cuerpo" }],
  ["rect", { x: "5", y: "4", width: "8", height: "7", rx: "1", key: "pantalla" }],
  ["path", { d: "M2 16h20", key: "cajon" }],
  ["path", { d: "M16 13.5h.01", key: "tecla1" }],
  ["path", { d: "M19 13.5h.01", key: "tecla2" }],
  ["path", { d: "M9 18.5h6", key: "tirador" }],
]);

/**
 * **Contabilidad — el libro mayor.** Un libro cerrado con una **cuenta T** dibujada en la tapa: la raya
 * horizontal y la vertical que parten el debe del haber.
 *
 * ⚠️ **No es un libro abierto, y es a propósito.** El libro abierto ya es el icono de «guías» y «Centro
 * de ayuda» en el landing y en Ayuda: Contabilidad habría quedado con el mismo dibujo que la
 * documentación. La cuenta T no la usa nada más y es el símbolo universal de la partida doble.
 */
export const IconoContabilidad = createLucideIcon("IconoContabilidad", [
  ["path", { d: "M4 19.5A2.5 2.5 0 0 1 6.5 17H20", key: "lomo" }],
  ["path", { d: "M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z", key: "tapa" }],
  ["path", { d: "M9 6h10", key: "cuenta-t-horizontal" }],
  ["path", { d: "M14 6v8", key: "cuenta-t-vertical" }],
]);

/**
 * **Nómina — la colilla de pago.** El recibo con el borde de abajo rasgado y, dentro, la silueta de una
 * persona.
 *
 * ⚠️ **El borde rasgado va solo abajo.** Con los dos bordes dentados el dibujo se lee como una factura
 * cualquiera; rasgado solo abajo es una colilla. Y **la persona tiene que ir grande**: con la cabeza más
 * pequeña, a 16px desaparecía y quedaba un recibo pelado, que es justo el icono de facturas.
 */
export const IconoNomina = createLucideIcon("IconoNomina", [
  ["path", { d: "M4 3h16v18l-2.66-1.6L14.66 21 12 19.4 9.33 21l-2.67-1.6L4 21z", key: "colilla" }],
  ["circle", { cx: "12", cy: "9", r: "2.6", key: "cabeza" }],
  ["path", { d: "M7.5 16a4.5 4.5 0 0 1 9 0", key: "hombros" }],
]);

/**
 * Los tres por clave de sistema — **la misma clave que usa `sistema_planes.sistema` en la base
 * compartida**, así que sirve para indexar directamente desde cualquier mapa de sistemas.
 *
 * 🔑 **Es la pieza que mata los literales sueltos.** Un `SISTEMA_META` que escribe `icon: ShoppingCart`
 * puede desincronizarse del de al lado sin que nada falle; uno que escribe `icon: ICONOS_SISTEMA.pos`
 * no puede.
 */
export const ICONOS_SISTEMA = {
  pos: IconoPos,
  contabilidad: IconoContabilidad,
  nomina: IconoNomina,
} as const;
